import { useMemo } from 'react'

import { matchFilterNumber } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CustomShortCodeRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['customShortcodes']

export type CustomShortcodesFilters = {
  owner_id?: number,
  owner_type?: string,
}

interface UseEntitiesOptions {
  filters?: CustomShortcodesFilters,
  performHttpRequests?: boolean,
  requestOptions?: CustomShortCodeRequestOptions,
}

function useCustomShortcodes(options: UseEntitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const { owner_id: filterOwnerId, owner_type: filterOwnerType } = filters

  const {
    updatedEntities: { customShortcodes: customShortcodesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const customShortcodes = useSelector(reduxState => reduxState.entities.customShortcodes)

  const filteredCustomShortcodes = useMemo(() => (
    Object.values(customShortcodes).filter((customShortcode) => {
      const { owner_id, owner_type } = customShortcode

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      return matchOwnerId && matchOwnerType
    })
  ), [customShortcodesUpdatedAt, JSON.stringify(filters)])

  const filteredCustomShortcodesCount = filteredCustomShortcodes.length
  const hasFilteredCustomShortcodes = !!filteredCustomShortcodesCount

  const entityKey = filterOwnerType && filterOwnerId ? `${filterOwnerType}${filterOwnerId}` : undefined
  const performHttpRequests = !!options.performHttpRequests && !!entityKey

  const { loading: loadingCustomShortcodes } = useReduxAction(
    'customShortcodes',
    'loadCustomShortcodes',
    {
      ...requestOptions,
      ...filters,
      entityKey,
      limit: 100,
    },
    [entityKey, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    filteredCustomShortcodes,
    filteredCustomShortcodesCount,
    hasFilteredCustomShortcodes,
    loading: loadingCustomShortcodes,
  }
}

export default useCustomShortcodes
